import * as R from 'ramda';
import { generateListTags, generateTag } from 'utils/helpers/fetch';
import dispatchingApi from '../../dispatchingApi';

/* example mock
queryFn: ({ params }) =>
  import('./mockData/viewDriverDetailsByLocation').then(({ default: getDrivers }) => ({
    data: getDrivers(params),
  })),
*/

const getUrl = R.concat('compliance/driver-status/');

const driverStatusEndpoints = dispatchingApi.injectEndpoints({
  endpoints: ({ mutation, query }) => ({
    addLocationToDriver: mutation({
      invalidatesTags: (result, error, { driverId, locationId }) => [
        { id: driverId, type: 'Driver' },
        { id: locationId, type: 'Location' },
      ],
      queryFn: ({ driverId, locationId }) =>
        import('./mockData/addLocationToDriver').then(({ default: addLocationToDriver }) => ({
          data: addLocationToDriver({ driverId, locationId }),
        })),
    }),

    createDriver: mutation({
      invalidatesTags: (result, error, { driver }) => [
        ...generateListTags('Location', 'locationId')(driver.locations),
        { id: 'LIST', type: 'Driver' },
      ],
      queryFn: ({ driver }) =>
        import('./mockData/createDriver').then(({ default: createDriver }) => ({
          data: createDriver({ driver }),
        })),
    }),

    getNewDriversForLocation: query({
      providesTags: (result, _, { locationId }) => [
        ...generateListTags('Driver', 'driverId', result),
        { id: locationId, type: 'Location' },
      ],
      queryFn: ({ locationId }) =>
        import('./mockData/newDriversForLocation').then(({ default: getDrivers }) => ({
          data: getDrivers(locationId),
        })),
    }),

    getNewLocationsForDriver: query({
      providesTags: (result, _, { driverId }) => [
        ...generateListTags('Location', 'locationId', result),
        { id: driverId, type: 'Driver' },
      ],
      queryFn: ({ driverId }) =>
        import('./mockData/newLocationsForDriver').then(({ default: getDrivers }) => ({
          data: getDrivers(driverId),
        })),
    }),

    getStates: query({
      keepUnusedDataFor: 3600, // 1 hour
      queryFn: () =>
        import('./mockData/getStates').then(({ default: getStates }) => ({
          data: getStates(),
        })),
    }),

    locationRemoveDriver: mutation({
      invalidatesTags: (result, _, { driverId, locationId }) => [
        { id: driverId, type: 'Driver' },
        { id: locationId, type: 'Location' },
      ],
      query: ({ locationId, driverId }) => ({
        body: { driverId, locationId },
        method: 'POST',
        url: getUrl('location-remove-driver'),
      }),
    }),

    viewDriver: query({
      providesTags: generateTag('Driver', 'driverId'),
      query: ({ driverId }) => ({
        params: { driverId },
        url: getUrl('view-driver'),
      }),
    }),

    viewDriverDetailsByLocation: query({
      providesTags: (result, _, { locationId }) => [
        ...generateListTags('Driver', 'driverId', result),
        { id: locationId, type: 'Location' },
      ],
      query: ({ locationId }) => ({
        params: { locationId },
        url: getUrl('view-details-by-location'),
      }),
    }),

    viewDriverSummaryByDriver: query({
      providesTags: generateListTags('Driver', 'driverId'),
      query: () => ({
        url: getUrl('view-summary-by-driver'),
      }),
    }),

    viewDriverSummaryByLocation: query({
      providesTags: generateListTags('Location', 'locationId'),
      query: () => ({
        url: getUrl('view-summary-by-location'),
      }),
    }),

    viewLocationDetailsByDriver: query({
      providesTags: (result, _, { driverId }) => [
        ...generateListTags('Location', 'location.locationId', result),
        { id: driverId, type: 'Driver' },
      ],
      query: ({ driverId }) => ({
        params: { driverId },
        url: getUrl('view-details-by-driver'),
      }),
    }),
  }),
});

export const {
  useAddLocationToDriverMutation,
  useCreateDriverMutation,
  useGetNewDriversForLocationQuery,
  useGetNewLocationsForDriverQuery,
  useGetStatesQuery,
  useLocationRemoveDriverMutation,
  useViewDriverQuery,
  useViewDriverSummaryByDriverQuery,
  useViewDriverSummaryByLocationQuery,
  useViewDriverDetailsByLocationQuery,
  useViewLocationDetailsByDriverQuery,
} = driverStatusEndpoints;
